<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { BackgroundType, MessageVariety, SizesType } from '@ui/styles'
import type { Placements } from '@ui/utils/placements'
import type { InputMessageValue } from './InputMessageTypes'
import Divider from '@ui/components/divider/Divider.vue'
import IconButton from '@ui/components/icon/IconButton.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import { ref } from 'vue'
import Times from '~icons/cue-regular/times'
import Icon from '../icon/Icon.vue'
import InputContainer from './InputContainer.vue'

type InputMode = '' | 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'

withDefaults(
  defineProps<{
    size?: SizesType
    type?: string
    label?: string
    placeholder?: string | number
    tooltip?: string
    message?: InputMessageValue
    variety?: MessageVariety
    clean?: boolean
    disabled?: boolean
    readonly?: boolean
    iconRight?: IconName
    background?: BackgroundType
    open?: Placements | boolean
    required?: boolean
    autocomplete?: string
    inputmode?: InputMode
    pattern?: string
    id?: string
    autocapitalize?: string
    maxLength?: number
    loading?: boolean
  }>(),
  {
    size: Sizes.Luke,
    type: 'text',
    label: '',
    placeholder: '',
    tooltip: '',
    message: '',
    clean: true,
    disabled: false,
    readonly: false,
    open: false,
    required: false,
    inputmode: '',
    pattern: '',
    loading: false,
  },
)

defineEmits(['focus', 'click:input', 'click:rightIcon', 'update:modelValue', 'clear'])

defineSlots<{
  'input'?: () => void
  'left:part'?: () => void
  'left'?: () => void
  'right'?: () => void
  'right:part'?: () => void
  'additional'?: () => void
}>()

const model = defineModel<string | number | null>({ default: '' })

const container = ref()
const nativeInputRef = ref<HTMLInputElement | null>(null)
defineExpose({
  container,
  focus: () => nativeInputRef.value?.focus(),
})
</script>

<template lang="pug">
InputContainer.ui-input.w-100p.single-line(
  ref="container"
  :class="[$slots['left:part'] && 'left', $slots['right:part'] && 'right']"
  :label="label"
  :tooltip="tooltip"
  :message="message"
  :size="size"
  :variety="variety"
  :disabled="disabled"
  :readonly="readonly"
  :background="background"
  :open="open"
  :required="required"
  :loading="loading"
  @click:input="$emit('click:input')"
)
  slot(name="left:part")
  .input-content.flex-between-center.w-100p(@click="!disabled && $emit('focus')")
    .left.flex(v-if="$slots.left")
      slot(name="left")
    slot(name="input")
      input.input(
        :id="id"
        ref="nativeInputRef"
        v-model="model"
        :type="type"
        :maxLength="maxLength"
        :disabled="disabled || readonly"
        :placeholder="placeholder === '' ? '' : $t(String(placeholder))"
        :autocomplete="autocomplete"
        :inputmode="inputmode || undefined"
        :pattern="pattern || undefined"
        :autocapitalize="autocapitalize"
      )
    .right.flex(v-if="$slots.right || iconRight")
      slot(name="right")
        Icon.icon-right.s-20(v-if="iconRight" :name="iconRight" @click="!disabled && $emit('click:rightIcon')")
    .clearable.flex.row-12(v-if="clean && !readonly && !disabled && modelValue")
      Divider(type="vertical" variant="base")
      IconButton.icon(:name="Times" :disabled="disabled" :variant="ButtonVariant.Function" @click="model = ''; $emit('clear')")
  slot(name="right:part")
  template(#additional)
    slot(name="additional")
</template>

<style scoped src="../../styles/css/input-sizes.css"></style>

<style scoped src="../../styles/css/input-field.css"></style>

<style scoped>
.right {
  color: var(--cue-content-neutral-secondary);
  --uno: typ-16-24-400;
}

.ui-input {
  &.disabled .right {
    color: var(--cue-content-neutral-disabled);
  }
}

.icon-right {
  color: var(--cue-content-neutral-tertiary);
  flex-shrink: 0;
}
</style>
